/**
 * #758 — the trader's own activity in one spread/total strip, as a list of
 * LINES rather than a pair of totals.
 *
 * The strip already knew all of this. It aggregated positions and open orders
 * across every line in the row and rendered two rings plus a run of plain
 * text, so a trader could see *that* they had exposure somewhere in the game
 * and had no way to get to it but stepping the three-chip window across the
 * ladder one arrow at a time. This turns the same facts into a list that can
 * be navigated.
 *
 * ─── Why this reads from the ring's own walk ───────────────────────────────
 *
 * The counts behind the rings come from a single pass over `openOrders` and
 * `positionByTicker` in `SpreadsTotalsTable`. This builder is handed the
 * result of THAT pass rather than repeating it. Two walks would be two
 * answers, and the failure mode is specific and bad: the ring says you have
 * activity, the list beside it does not list it, and the trader is left
 * deciding which of the two lies. Equivalence by construction — one source,
 * two presentations — is the same rule the compact bands follow.
 *
 * ─── `idx: null` is a real state, not an error ─────────────────────────────
 *
 * The strip deliberately counts orders on strikes Hydra never surfaced —
 * `SpreadsTotalsTable` says so at the aggregation: *"bets on strikes hydra
 * hasn't surfaced still count toward the ring"*. Those have no chip, so there
 * is nothing to select and no index to hand back. They still belong in the
 * list: the ring beside it is already counting them, so dropping them would
 * make the list contradict the ring — the exact disagreement above. They are
 * listed and inert.
 *
 * Positions cannot land in that state today (they are only counted for
 * tickers the board surfaced), which is why every held line is reachable and
 * only some order lines are. That asymmetry is a property of the walk, not of
 * this module, so it is asserted in the tests rather than assumed here.
 */
import type { UserPositionSlice } from '@/hooks/useUserKalshiPOS';

/** What one pass over the trader's activity learned about a single ticker. */
export interface StripActivityFact {
  /** The line this ticker sits on; null when it could not be resolved. */
  line: number | null;
  /** Held contracts, or null when the trader holds none on this ticker. */
  position: UserPositionSlice | null;
  /** Resting orders on this ticker, split by side. */
  ordersYes: number;
  ordersNo: number;
}

/** One navigable row of the activity list. */
export interface StripActivityLine extends StripActivityFact {
  /**
   * The market ticker. This is the IDENTITY of the row and the only thing
   * that may be used to correlate it with anything else — never the line
   * value (two sides of a spread ladder share a magnitude) and never the
   * position in this array (the feed reorders lines under a mounted row).
   */
  ticker: string;
  /**
   * Index into the strip's line array, or null when the board does not list
   * this market. Null means the row renders but cannot be selected.
   */
  idx: number | null;
}

/**
 * Merge one strip's activity facts into the list the row renders.
 *
 * `idxByTicker` decides reachability: a ticker the board lists resolves to its
 * position in the strip, and anything else stays null. The caller derives that
 * map from the same `event.spreads` / `event.totals` array the chips are built
 * from, so an entry's index and its chip can never point at different markets.
 */
export function buildStripActivityLines(
  facts: ReadonlyMap<string, StripActivityFact>,
  idxByTicker: ReadonlyMap<string, number>,
): StripActivityLine[] {
  const out: StripActivityLine[] = [];
  for (const [ticker, fact] of facts) {
    // A ticker with neither a position nor a working order is not activity.
    // The walk can produce one — an order that filled to zero remaining still
    // has an entry — and listing it would offer navigation to a market the
    // trader has nothing on.
    if (!hasActivity(fact)) continue;
    out.push({ ...fact, ticker, idx: idxByTicker.get(ticker) ?? null });
  }

  // Ascending by line, matching the chip order the list navigates into, so the
  // list and the strip read left-to-right the same way. A line that could not
  // be resolved sorts last rather than being dropped — the holding is real,
  // which is the rule the held-contracts hover already follows. Ties break on
  // ticker so the order is total and cannot flicker between renders: two sides
  // of a spread ladder legitimately share a magnitude, and `line` alone would
  // leave their relative order down to Map insertion.
  return out.sort((a, b) => {
    const al = a.line ?? Number.POSITIVE_INFINITY;
    const bl = b.line ?? Number.POSITIVE_INFINITY;
    if (al !== bl) return al - bl;
    return a.ticker < b.ticker ? -1 : a.ticker > b.ticker ? 1 : 0;
  });
}

/** True when the trader holds contracts or has a working order on this line. */
export function hasActivity(fact: StripActivityFact): boolean {
  const held = (fact.position?.yesCount ?? 0) + (fact.position?.noCount ?? 0);
  return held > 0 || fact.ordersYes + fact.ordersNo > 0;
}

/** True when the row can be selected — i.e. the board lists this market. */
export function isReachable(line: StripActivityLine): boolean {
  return line.idx != null;
}

/**
 * The set of tickers carrying activity, for marking chips already on screen.
 *
 * A Set of TICKERS, not of line values or indices: the chip strip resolves a
 * chip's identity the same way, so a mark can never land on the neighbouring
 * strike when the feed reorders the ladder.
 */
export function activeTickerSet(lines: readonly StripActivityLine[]): Set<string> {
  return new Set(lines.map((l) => l.ticker));
}

/**
 * One row's summary, e.g. `40 YES · 2 orders`.
 *
 * Deliberately NOT a price or an average. The 'C' ring's hover already gives
 * the per-line entry basis and is single-sourced in `heldContractsTitle`;
 * restating a number here would be a second place for it to be right, and the
 * two would drift the first time either changed. This says what you have and
 * how much is working — enough to choose a line, and nothing that has to agree
 * with a money surface.
 */
export function activitySummaryText(line: StripActivityLine): string {
  const parts: string[] = [];
  const yes = line.position?.yesCount ?? 0;
  const no = line.position?.noCount ?? 0;
  if (yes > 0) parts.push(`${formatCount(yes)} YES`);
  if (no > 0) parts.push(`${formatCount(no)} NO`);
  const orders = line.ordersYes + line.ordersNo;
  if (orders > 0) parts.push(`${orders} order${orders === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

/**
 * Whole contracts, matching the count convention every other surface uses.
 *
 * Kalshi fills are fractional, so a sold-down position arrives carrying binary
 * float dust ("22.099999999999998"). `fmtQty` is the project's display rule
 * for a contract count and #716 settled that rounding it is correct here; this
 * mirrors it rather than importing, because this string is a chooser label and
 * must not acquire a dependency on a money-formatting module.
 */
function formatCount(n: number): string {
  return Math.round(n).toLocaleString();
}
